import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import BulkUploadModal from '../components/BulkUploadModal';
import axios from 'axios';
import { Calendar, Clock, TrendingUp, AlertCircle, Users, CheckCircle, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const [activeCompany] = useActiveCompany();
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [stats, setStats] = useState({
    present_today: 0,
    absent_today: 0,
    on_leave: 0,
    late_arrivals: 0,
    pending_regularizations: 0,
    pending_ot_approvals: 0,
  });
  const [asOf, setAsOf] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const qs = activeCompany ? `?company_id=${activeCompany}` : '';
      const { data } = await axios.get(
        `${API_URL}/dashboards/attendance/today-stats${qs}`,
        { withCredentials: true },
      );
      setStats({
        present_today: data.present_today || 0,
        absent_today: data.absent_today || 0,
        on_leave: data.on_leave || 0,
        late_arrivals: data.late_arrivals || 0,
        pending_regularizations: data.pending_regularizations || 0,
        pending_ot_approvals: data.pending_ot_approvals || 0,
      });
      setAsOf(data.as_of || '');
    } catch (error) {
      console.error('Failed to fetch attendance stats:', error);
      toast.error('Failed to load attendance stats');
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    {
      label: 'Present Today',
      value: stats.present_today,
      icon: CheckCircle,
      color: 'bg-green-50 text-green-600 border-green-200',
    },
    {
      label: 'Absent Today',
      value: stats.absent_today,
      icon: AlertCircle,
      color: 'bg-red-50 text-red-600 border-red-200',
    },
    {
      label: 'On Leave',
      value: stats.on_leave,
      icon: Calendar,
      color: 'bg-blue-50 text-blue-600 border-blue-200',
    },
    {
      label: 'Late Arrivals',
      value: stats.late_arrivals,
      icon: Clock,
      color: 'bg-amber-50 text-amber-600 border-amber-200',
    },
    {
      label: 'Pending Regularizations',
      value: stats.pending_regularizations,
      icon: TrendingUp,
      color: 'bg-purple-50 text-purple-600 border-purple-200',
    },
    {
      label: 'Pending OT Approvals',
      value: stats.pending_ot_approvals,
      icon: Users,
      color: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Clock className="inline-block w-8 h-8 mr-2 mb-1" />
              Attendance Dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Real-time attendance monitoring and analytics
              {asOf && <span className="ml-2 text-xs text-slate-400" data-testid="attendance-stats-as-of">· as of {asOf}</span>}
            </p>
          </div>
          <div className="flex gap-3">
            <button data-testid="attendance-dashboard-bulk-upload-btn" onClick={() => setShowBulkUpload(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-md text-sm font-medium">
              <Upload className="w-4 h-4" /> Bulk Upload
            </button>
            <button data-testid="attendance-export-btn" onClick={() => {
            const exportData = statCards.map(c => ({ Metric: c.label, Value: c.value }));
            downloadCSV(exportData, 'attendance_summary.csv');
            toast.success('Attendance summary exported');
          }} className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-md text-sm font-medium">
            <Download className="w-4 h-4" /> Export Summary
          </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`bg-white border rounded-lg p-6 hover:shadow-sm transition-shadow ${card.color}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-70">
                      {card.label}
                    </p>
                    <p className="mt-2 text-3xl font-semibold font-mono">
                      {loading ? '-' : card.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg bg-white`}>
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              data-testid="quick-action-daily-register"
              onClick={() => navigate('/attendance/daily')}
              className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left"
            >
              <p className="font-medium text-slate-900">Daily Register</p>
              <p className="text-xs text-slate-500 mt-1">View today's attendance</p>
            </button>
            <button
              data-testid="quick-action-punch-logs"
              onClick={() => navigate('/attendance/punch-logs')}
              className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left"
            >
              <p className="font-medium text-slate-900">Punch Logs</p>
              <p className="text-xs text-slate-500 mt-1">View all punch entries</p>
            </button>
            <button
              data-testid="quick-action-regularization"
              onClick={() => navigate('/attendance/regularization')}
              className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left"
            >
              <p className="font-medium text-slate-900">Regularization Inbox</p>
              <p className="text-xs text-slate-500 mt-1">Approve requests</p>
            </button>
            <button
              data-testid="quick-action-exceptions"
              onClick={() => navigate('/attendance/exceptions')}
              className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left"
            >
              <p className="font-medium text-slate-900">Exceptions</p>
              <p className="text-xs text-slate-500 mt-1">Review attendance issues</p>
            </button>
          </div>
        </div>

        {/* Recent Activity — populated from real audit log; empty state when no activity */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="attendance-recent-activity">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Recent Activity</h2>
          <p className="text-sm text-slate-400 italic">
            No recent attendance activity yet. Activity will appear here once you upload attendance, calculate periods, or process regularization requests.
          </p>
        </div>

        <BulkUploadModal
          open={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          title="Bulk Attendance Upload"
          description="Upload a CSV to log manual attendance records for multiple employees."
          templateUrl="/attendance/bulk-upload/template"
          uploadUrl="/attendance/bulk-upload"
          templateFileName="attendance_bulk_upload_template.csv"
          onUploadSuccess={() => toast.success('Attendance records uploaded successfully')}
          columns={['employee_code', 'attendance_date', 'attendance_status', 'in_time', 'out_time', 'remarks']}
        />
      </div>
    </MainLayout>
  );
};

export default AttendanceDashboard;
